/**
 * Service de gestion des paramètres d'activité
 * 
 * Ce service gère les paramètres spécifiques à une activité :
 * - Informations du commerce
 * - Logo
 * - Paramètres généraux (devise, langue)
 * - Numérotation des tickets
 * 
 * IMPORTANT: Il ne doit y avoir qu'un seul enregistrement dans la table activitySettings
 */

import { BaseService } from './base.service';

export class ActivitySettingsService extends BaseService {
  constructor() {
    super('activitySettings');
  }

  /**
   * Récupère les paramètres de l'activité (le seul enregistrement)
   * @returns {Promise<Object|null>} Les paramètres de l'activité ou null si aucun
   */
  async getActivitySettings() {
    try {
      const settings = await this.getAll();

      if (settings.length === 0) {
        console.log('⚠️ Aucun paramètre trouvé');
        return null;
      }

      if (settings.length > 1) {
        console.error('❌ ERREUR: Il y a plusieurs enregistrements dans activitySettings, ce qui n\'est pas autorisé.');
        throw new Error('Il ne doit y avoir qu\'un seul enregistrement dans activitySettings');
      }

      // Un seul enregistrement, on le retourne
      return settings[0];
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des paramètres:', error);
      throw error;
    }
  }

  /**
   * Crée les paramètres initiaux de l'activité (un seul enregistrement autorisé)
   * @param {Object} settings - Paramètres initiaux
   * @returns {Promise<Object>} Les paramètres créés
   */
  async createActivitySettings(settings) {
    try {
      console.log('🆕 Création des paramètres d\'activité');
      
      // Vérifier s'il existe déjà des enregistrements
      const existingSettings = await this.getAll();
      
      if (existingSettings.length > 0) {
        console.error('❌ ERREUR: Impossible de créer - il existe déjà des paramètres d\'activité');
        console.error('📦 Enregistrements existants:', existingSettings);
        throw new Error('Il ne peut y avoir qu\'un seul enregistrement de paramètres d\'activité');
      }

      // Préparer les données avec timestamps
      const newSettings = {
        ...settings,
        createdAt: new Date(),
        updatedAt: new Date(),
        lastSync: new Date()
      };

      console.log('📝 Création du nouvel enregistrement:', newSettings);
      
      // Créer l'enregistrement
      const createdId = await this.add(newSettings);
      console.log('✅ Paramètres créés avec l\'ID:', createdId);
      
      return { ...newSettings, id: createdId };
    } catch (error) {
      console.error('❌ Erreur lors de la création des paramètres:', error);
      throw error;
    }
  }

  /**
   * Met à jour les paramètres de l'activité
   * @param {Object} settings - Nouveaux paramètres
   * @returns {Promise<Object>} Les paramètres mis à jour
   */
  async updateActivitySettings(settings) {
    console.log('🔄 updateActivitySettings - Mise à jour des paramètres');
    
    try {
      // Récupérer l'enregistrement actuel (avec nettoyage automatique si nécessaire)
      const currentSettings = await this.getActivitySettings();
      
      if (!currentSettings) {
        console.error('❌ Aucun paramètre trouvé');
        throw new Error('Aucun paramètre trouvé');
      }

      // Préparer les données mises à jour
      const updatedSettings = {
        ...currentSettings,
        ...settings,
        updatedAt: new Date()
      };

      console.log('📝 Paramètres à mettre à jour:', updatedSettings);
      
      // Mettre à jour l'enregistrement existant
      await this.update(currentSettings.id, updatedSettings);
      console.log('✅ Paramètres mis à jour avec succès');
      
      return updatedSettings;
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour des paramètres:', error);
      throw error;
    }
  }

  /**
   * Supprime tous les enregistrements et en crée un nouveau
   * @param {Object} settings - Nouveaux paramètres
   * @returns {Promise<Object>} Les nouveaux paramètres
   */
  async resetActivitySettings(settings) {
    try {
      console.log('🔄 Reset complet des paramètres d\'activité');
      
      // Supprimer tous les enregistrements existants
      await this.clear();
      console.log('🗑️ Tous les enregistrements supprimés');
      
      // Créer le nouvel enregistrement
      return await this.createActivitySettings(settings);
    } catch (error) {
      console.error('❌ Erreur lors du reset des paramètres:', error);
      throw error;
    }
  }

  /**
   * Vérifie l'intégrité de la table (un seul enregistrement)
   * @returns {Promise<boolean>} true si la table est valide
   */
  async validateTableIntegrity() {
    try {
      const allSettings = await this.getAll();
      
      if (allSettings.length === 0) {
        console.log('⚠️ Table vide - aucun paramètre trouvé');
        return false;
      }
      
      if (allSettings.length > 1) {
        console.error('❌ ERREUR: Plusieurs enregistrements trouvés dans activitySettings');
        console.error('📦 Nombre d\'enregistrements:', allSettings.length);
        console.error('📦 Enregistrements:', allSettings);
        return false;
      }
      
      console.log('✅ Intégrité de la table validée - un seul enregistrement');
      return true;
    } catch (error) {
      console.error('❌ Erreur lors de la validation de l\'intégrité:', error);
      return false;
    }
  }

  /**
   * Met à jour le logo de l'activité
   * @param {File} logoFile - Fichier du logo
   * @returns {Promise<Object>} Les paramètres mis à jour
   */
  async updateLogo(logoFile) {
    try {
      // TODO: Implémenter le stockage du logo (par exemple, avec Firebase Storage)
      // Pour l'instant, on utilise URL.createObjectURL comme placeholder
      const logoUrl = URL.createObjectURL(logoFile);

      const settings = await this.getActivitySettings();
      settings.businessInfo.logo = logoUrl;
      
      return this.updateActivitySettings(settings);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du logo:', error);
      throw error;
    }
  }

  /**
   * Récupère le prochain numéro de ticket
   * @returns {Promise<string>} Le prochain numéro de ticket
   */
  async getNextTicketNumber() {
    try {
      const settings = await this.getActivitySettings();
      const { ticketNumbering } = settings;
      const { type, prefix, suffix, currentNumber } = ticketNumbering;

      // Formater le numéro selon le type de numérotation
      let formattedNumber = currentNumber.toString().padStart(4, '0');
      
      if (type === 'monthly') {
        const date = new Date();
        formattedNumber = `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, '0')}${formattedNumber}`;
      } else if (type === 'yearly') {
        formattedNumber = `${new Date().getFullYear()}${formattedNumber}`;
      }

      // Ajouter le préfixe et le suffixe
      const ticketNumber = `${prefix}${formattedNumber}${suffix}`;

      // Mettre à jour le numéro courant
      ticketNumbering.currentNumber = currentNumber + 1;
      await this.updateActivitySettings({ ticketNumbering });

      return ticketNumber;
    } catch (error) {
      console.error('Erreur lors de la génération du numéro de ticket:', error);
      throw error;
    }
  }

  /**
   * Réinitialise la numérotation des tickets
   * @param {number} startNumber - Nouveau numéro de départ
   * @returns {Promise<Object>} Les paramètres mis à jour
   */
  async resetTicketNumbering(startNumber = 1) {
    try {
      const settings = await this.getActivitySettings();
      settings.ticketNumbering.currentNumber = startNumber;
      return this.updateActivitySettings(settings);
    } catch (error) {
      console.error('Erreur lors de la réinitialisation de la numérotation:', error);
      throw error;
    }
  }

  /**
   * Nettoie les doublons dans la table activitySettings
   * Garde seulement l'enregistrement le plus récent
   * @returns {Promise<Object>} L'enregistrement conservé
   */
  async cleanDuplicateSettings() {
    try {
      console.log('🧹 Nettoyage des doublons dans activitySettings');
      const allSettings = await this.getAll();
      console.log('📦 Tous les enregistrements trouvés:', allSettings);

      if (allSettings.length <= 1) {
        console.log('✅ Aucun doublon à nettoyer');
        return allSettings[0] || null;
      }

      // Trier par date de mise à jour (le plus récent en premier)
      const sortedSettings = allSettings.sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt);
        const dateB = new Date(b.updatedAt || b.createdAt);
        return dateB - dateA;
      });

      const latestSettings = sortedSettings[0];
      const duplicatesToDelete = sortedSettings.slice(1);

      console.log('🔍 Enregistrement le plus récent à conserver:', latestSettings);
      console.log('🗑️ Doublons à supprimer:', duplicatesToDelete);

      // Supprimer les doublons
      for (const duplicate of duplicatesToDelete) {
        await this.delete(duplicate.id);
        console.log(`🗑️ Supprimé l'enregistrement avec l'ID: ${duplicate.id}`);
      }

      console.log(`✅ Nettoyage terminé. ${duplicatesToDelete.length} doublons supprimés.`);
      return latestSettings;
    } catch (error) {
      console.error('❌ Erreur lors du nettoyage des doublons:', error);
      throw error;
    }
  }
}

export default new ActivitySettingsService(); 